import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { Typography } from '@mui/material';
import { userAPI } from '../../services/api';

// Liste des pays
const countries = [
  { code: 'FR', label: 'France' },
  { code: 'BE', label: 'Belgique' },
  { code: 'CH', label: 'Suisse' },
  { code: 'CA', label: 'Canada' },
  { code: 'DE', label: 'Allemagne' },
  { code: 'ES', label: 'Espagne' },
  { code: 'GB', label: 'Royaume-Uni' },
  { code: 'IT', label: 'Italie' },
  { code: 'US', label: 'États-Unis' },
  // Ajoutez d'autres pays selon vos besoins
].sort((a, b) => a.label.localeCompare(b.label));

const ProfileForm = () => {
  const { currentUser, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    wsdcId: '',
    role: 'follower',
    country: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialiser le formulaire avec les données utilisateur
  useEffect(() => {
    if (currentUser) {
      setFormData({
        firstName: currentUser.prenom || '',
        lastName: currentUser.nom || '',
        wsdcId: currentUser.id_wsdc || '',
        role: currentUser.role || 'follower',
        country: currentUser.pays || '',
        city: currentUser.ville || ''
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountryChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      country: newValue ? newValue.label : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      
      await updateProfile(formData);
      
      setSuccess('Profil mis à jour avec succès !');
    } catch (err) {
      setError(err.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
        INFORMATIONS PERSONNELLES
      </Typography>
      
      <Grid container spacing={2}>
        {/* Prénom et nom sur la même ligne */}
        <Grid item xs={6}>
          <TextField
            label="Prénom"
            name="firstName"
            fullWidth
            required
            value={formData.firstName}
            onChange={handleChange}
            variant="outlined"
            size="small"
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Nom"
            name="lastName"
            fullWidth
            required
            value={formData.lastName}
            onChange={handleChange}
            variant="outlined"
            size="small"
          />
        </Grid>
        
        {/* Rôle et WSDC ID sur la même ligne */}
        <Grid item xs={6}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">Rôle</FormLabel>
            <RadioGroup
              row
              name="role"
              value={formData.role}
              onChange={handleChange}
            >
              <FormControlLabel value="leader" control={<Radio />} label="Leader" />
              <FormControlLabel value="follower" control={<Radio />} label="Follower" />
            </RadioGroup>
          </FormControl>
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="WSDC ID (optionnel)"
            name="wsdcId"
            fullWidth
            value={formData.wsdcId}
            onChange={handleChange}
            variant="outlined"
            size="small"
            helperText="Si vous avez un numéro WSDC, veuillez l'indiquer ici"
          />
        </Grid>
        
        {/* Pays et ville sur la même ligne (non obligatoires) */}
        <Grid item xs={6}>
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.label}
            value={countries.find(country => country.label === formData.country) || null}
            onChange={handleCountryChange}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pays (optionnel)"
                fullWidth
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>
        
        <Grid item xs={6}>
          <TextField
            label="Ville (optionnelle)"
            name="city"
            fullWidth
            value={formData.city}
            onChange={handleChange}
            variant="outlined"
            size="small"
          />
        </Grid>
        
        {/* Bouton sur une ligne séparée */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 2, textTransform: 'uppercase' }}
            fullWidth
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'METTRE À JOUR LE PROFIL'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileForm;
